// ── Auth ──────────────────────────────────────────────────────────────────────

export type UserRole = 'owner' | 'admin' | 'member'

// Only owners and admins may use the account portal; members are refused at sign-in.
export const PORTAL_ROLES: readonly UserRole[] = ['owner', 'admin']

export function canAccessPortal(role: string | null | undefined): boolean {
  return PORTAL_ROLES.includes(role as UserRole)
}

// Higher rank = more permissions; mirrors qtz-account's role ranking.
export const ROLE_RANK: Record<UserRole, number> = { member: 1, admin: 2, owner: 3 }
export type OrgPlan = 'free' | 'starter' | 'pro' | 'enterprise'

export interface LoginRequest {
  email: string
  password: string
}

export interface RegisterRequest {
  org_name: string
  email: string
  password: string
}

// Returned directly by /auth/login, /auth/register, /auth/refresh (not wrapped)
export interface TokenPairResponse {
  access_token: string
  refresh_token: string
  expires_at: string
  token_type: 'Bearer'
}

// ── API envelope ──────────────────────────────────────────────────────────────

export interface ApiResponse<T> {
  success: boolean
  data: T
}

export interface MessageResponse {
  message: string
}

// ── Organization ──────────────────────────────────────────────────────────────

export interface Organization {
  id: string
  name: string
  plan: OrgPlan
  created_at: string
  updated_at: string
}

export interface UpdateOrgRequest {
  name?: string
  plan?: OrgPlan
}

// ── User ──────────────────────────────────────────────────────────────────────

export interface User {
  id: string
  organization_id: string
  email: string
  role: UserRole
  last_login_at: string | null
  created_at: string
}

// ── API Key ───────────────────────────────────────────────────────────────────

export interface ApiKey {
  id: string
  label: string
  created_at: string
  last_used_at: string | null
  created_by: string | null // user UUID — who issued the key
  owner_id: string | null // user UUID — who the key acts as; null on keys issued before owners
}

// Returned directly by POST /api-keys (not wrapped in {success,data})
export interface IssuedApiKey {
  id: string
  label: string
  key: string // plaintext — shown once, never stored
  created_at: string
  created_by: string | null
  owner_id: string | null
}

// ── LLM Billing ───────────────────────────────────────────────────────────────

// Go struct without json tags — fields serialize with capital letter names
export interface LLMUsageSummary {
  Period: string
  InputTokens: number
  OutputTokens: number
  CostUSD: number
}

export interface LLMUsageRecord {
  ID: string
  OrganizationID: string
  ScanJobID: string | null
  Provider: string
  Model: string
  InputTokens: number
  OutputTokens: number
  CostUSD: number
  FilesAnalyzed: number
  Tier: string
  CreatedAt: string
}

// TierUsageSummary has a json tag so it uses lowercase
export interface TierUsageSummary {
  files: number
}

export type TierBreakdown = Record<string, TierUsageSummary>

// Go struct without json tags — fields serialize with capital letter names
export interface LLMBudget {
  OrganizationID: string
  PeriodType: 'monthly' | 'daily'
  LimitUSD: number
  AlertPct: number
  UpdatedAt: string
}

export interface UpsertBudgetRequest {
  period_type: 'monthly' | 'daily'
  limit_usd: number
  alert_pct: number
}

export interface PriceEntry {
  model: string
  input_per_m_tokens_usd: number
  output_per_m_tokens_usd: number
}
